package com.example.mystybag.common.ui.components.custom

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.withTransform
import androidx.compose.ui.unit.Dp
import com.example.mystybag.common.extensions.modifyIf
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.composeColor
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.views.AppTheme
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.yield

private suspend fun progressFlow(initial: Float = 0f, step: Float = 0.01f): Flow<Float> {
    return flow {
        var progress = initial
        while (true) {
            emit(progress)
            progress += step
            if (progress > 1f)
                progress = 0f
            delay(10L)
            yield()
        }
    }
}

@Composable
fun StateProgressView(
    visible             : Boolean,
    modifier            : Modifier  = Modifier,
    size                : Dp        = p48,
    strokeWidth         : Dp        = p4,
    progressArcColor1   : Color     = AppTheme.current.colors.primary,
    progressArcColor2   : Color     = AppTheme.current.colors.primary,
    withDarkBackground  : Boolean   = true
) {
    val currentProgress = remember { mutableFloatStateOf(0f) }
    val overlayColor    = SR.colors.background_black.composeColor.copy(alpha = 0.6f)

    LaunchedEffect(visible) {
        if (visible) {
            progressFlow(currentProgress.floatValue).collect { value ->
                currentProgress.floatValue = value
            }
        }
    }

    AnimatedVisibility(
        visible = visible,
        enter   = fadeIn(animationSpec = tween(100, easing = LinearEasing)),
        exit    = fadeOut(animationSpec = tween(100, easing = LinearEasing))
    ) {
        Box(
            modifier         = Modifier
                .modifyIf(withDarkBackground) { background(overlayColor) }
                .fillMaxSize()
                .noRippleClickable {}
                .then(modifier),
            contentAlignment = Alignment.Center
        ) {
            Canvas(modifier = Modifier.size(size)) {
                val strokeWidthPx = strokeWidth.toPx()
                val arcSize       = size.toPx() - strokeWidthPx
                val gradientBrush = Brush.verticalGradient(
                    colors = listOf(progressArcColor1, progressArcColor2, progressArcColor1)
                )
                withTransform({
                    rotate(degrees = currentProgress.floatValue * 360, pivot = center)
                }) {
                    drawArc(
                        brush       = gradientBrush,
                        startAngle  = 0f,
                        sweepAngle  = 270f,
                        useCenter   = false,
                        topLeft     = Offset(strokeWidthPx / 2, strokeWidthPx / 2),
                        size        = Size(arcSize, arcSize),
                        style       = Stroke(width = strokeWidthPx, cap = StrokeCap.Round)
                    )
                }
            }
        }
    }
}